import { CommonErrorCode, WorkbookErrorCode, TagErrorCode } from '../../ts/helpers/ErrorCodes';
import { getAppStore } from '../../app/AppStore';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { IErrorDetail } from '../../commonInfra/contentActions/ActionMessages';
import { IErrorItem } from '../../commonInfra/contentActions/MultipleItemResult';
import { IWorkbookBase } from '../../ts/models/IWorkbookInfo';
import { L10n } from '../../ts/L10n/L10n';
import { Messages } from '../../codegen/Localize';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { DestinationItemType } from '@tableau/nested-project-explorer';


function errorDetailsFor(workbookErrorItem: IErrorItem<IWorkbookBase>): IErrorDetail {
  return {
    itemName: workbookErrorItem.item.name,
    itemUrl:
      workbookErrorItem.error.code === CommonErrorCode.WorkbookNotFound
        ? null
        : workbookUrl(workbookErrorItem.item),
    errorMessage: errorMessageFor(workbookErrorItem)
  };
}

function workbookUrl(workbook: IWorkbookBase): string {
  const siteUrls = siteUrlsSelector(getAppStore().getState());
  return siteUrls ? siteUrls.workbookUrl(workbook.id) : '';
}

function errorMessageFor(errorItem: IErrorItem<IWorkbookBase>) {
  const workbook = errorItem.item;
  switch (errorItem.error.code) {
    case WorkbookErrorCode.WorkbookDescriptionTooLong:
      return Messages.errorWorkbookDescriptionTooLong({ MAX_LENGTH: L10n.localizedCount(getSessionManagementServiceInstance().maxDescriptionLength()) });
    case CommonErrorCode.WorkbookNotFound:
      return Messages.errorWorkbookNotFound({ WORKBOOK_NAME: L10n.elideIfLong(workbook.name) });
    case WorkbookErrorCode.WorkbookDisabled:
      return Messages.errorWorkbookDisabled({ WORKBOOK_NAME: L10n.elideIfLong(workbook.name) });
    case WorkbookErrorCode.WorkbookNameAlreadyExistsInDestinationProject:
      return Messages.errorWorkbookNameAlreadyExistsInDestinationProject();
    case WorkbookErrorCode.WorkbookDestinationProjectNotFound:
      return Messages.errorWorkbookDestinationProjectNotFound();
    case WorkbookErrorCode.WorkbookNameAlreadyExists:
      return Messages.errorWorkbookNameAlreadyExists();
    case TagErrorCode.TooManyTags:
      return Messages.changeTagsErrorTooManyTags();
    // Permissions errors
    // TODO: Do we want specific error messages for each of these cases?
    case WorkbookErrorCode.WorkbookMoveInsufficientPermissionsOnDestinationProject:
    case WorkbookErrorCode.WorkbookMoveInsufficientPermissionsOnSourceProject:
    case WorkbookErrorCode.WorkbookMoveInsufficientPermissionsOnWorkbook:
      return Messages.errorWorkbookMoveInsufficientPermissions();
    case CommonErrorCode.CallingUserInsufficientPermissions:
      return Messages.errorWorkbookInsufficientPermissions();

    default:
      return Messages.errorUnknown();
  }
}

function moveContentToContainerErrorMessageDetailsFor(workbookErrorItem: IErrorItem<IWorkbookBase>, destinationType: DestinationItemType): IErrorDetail {
  return {
    itemName: workbookErrorItem.item.name,
    itemUrl:
      workbookErrorItem.error.code === CommonErrorCode.WorkbookNotFound
        ? null
        : workbookUrl(workbookErrorItem.item),
    errorMessage: moveContentToContainerErrorMessageFor(workbookErrorItem, destinationType)
  };
}

function moveContentToContainerErrorMessageFor(errorItem: IErrorItem<IWorkbookBase>, destinationType: DestinationItemType) {
  const workbook = errorItem.item;
  switch (errorItem.error.code) {
    case CommonErrorCode.WorkbookNotFound:
      return Messages.errorWorkbookNotFound({ WORKBOOK_NAME: L10n.elideIfLong(workbook.name) });
    case WorkbookErrorCode.WorkbookNameAlreadyExistsInDestinationProject:
      return destinationType === DestinationItemType.personalSpace ?
         Messages.errorWorkbookNameAlreadyExistsInPersonalSpace():
         Messages.errorWorkbookNameAlreadyExistsInDestinationProject();

    case WorkbookErrorCode.WorkbookMoveInsufficientPermissionsOnDestinationProject:
    case WorkbookErrorCode.WorkbookMoveInsufficientPermissionsOnSourceProject:
    case WorkbookErrorCode.WorkbookMoveInsufficientPermissionsOnWorkbook:
    case CommonErrorCode.MoveInsufficientPermissionsOnContent:
        Messages.errorWorkbookMoveInsufficientPermissions();
    case CommonErrorCode.CallingUserInsufficientPermissions:
      return Messages.errorWorkbookInsufficientPermissions();

    default:
      return Messages.errorUnknown();
  }
}


export const WorkbookErrors = {
  errorDetailsFor,
  errorMessageFor,
  moveContentToContainerErrorMessageDetailsFor
};

// Easy type definition that is used in some cases.
export type WorkbookErrors = typeof WorkbookErrors;